import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Project, UserRole } from 'common/contracts';
import { IPermissions } from 'common/permissions.types';
import { ELocalStorageKeys } from 'constants/enums';
import { UserDetails } from 'pages/Login/Login.types';
import { localStorageUtil } from 'utils/localStorageUtil';

import { EApiRoutes } from './api.utils';

export default function usePermissions(permissions?: any) {
  const queryClient = useQueryClient();
  
  const userDetails = queryClient.getQueryData<UserDetails>([EApiRoutes.USERS]);

  const getPermissions = () => {
    return localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS);
  };

  const setPermissions = () => {
    const cachedUserDetails = queryClient.getQueryData<UserDetails>([EApiRoutes.USERS]);
    const projects = cachedUserDetails?.projects;
    
    if (!projects) {
      return;
    }
    if (projects) {
      const returnedProjects: any = {};
      projects.forEach((p: any) => {
        returnedProjects[p.publisherId] = {};
        p.permissions.forEach((permission: any) => {
          const splittedPermission = permission.split(':');
          const permissionValue = splittedPermission[1] + (splittedPermission[2] ? `:${splittedPermission[2]}` : '');
          if (returnedProjects[p.publisherId][splittedPermission[0]]) {
            returnedProjects[p.publisherId][splittedPermission[0]].push(
              permissionValue
            );
          } else {
            returnedProjects[p.publisherId][splittedPermission[0]] = [
              permissionValue
            ];
          }
        });
      });

      localStorageUtil.set(ELocalStorageKeys.PERMISSIONS, returnedProjects);
    }
  };

  const canUserEdit = useMemo(() => {
    const userRole = userDetails?.role;
    return (
      userRole === UserRole.ADMIN ||
      userRole === UserRole.OPERATIONS_EDITOR ||
      userRole === UserRole.SUPER_ADMIN
    );
  }, [userDetails?.role]);

  const isAdmin = useMemo(() => {
    const userRole = userDetails?.role;
    return userRole === UserRole.ADMIN || userRole === UserRole.SUPER_ADMIN;
  }, [userDetails?.role]);

  const isSuperAdmin = useMemo(() => {
    return userDetails?.role === UserRole.SUPER_ADMIN;
  }, [userDetails?.role]);

  const isViewerOrEditor = useMemo(() => {
    return userDetails?.role === UserRole.OPERATIONS_EDITOR;
  }, [userDetails?.role]);

  const isSuperAdminByProject = useCallback((activeProjectId: string) => {
    const projectDetails = userDetails?.projects;
    const userRoleByCurrentProject = projectDetails?.find(
      (project: Project) => project.publisherId === activeProjectId
    )?.role;
    return userRoleByCurrentProject === UserRole.SUPER_ADMIN;
  }, [userDetails?.projects]);

  const isAdminByProject = useCallback((activeProjectId: string) => {
    const projectDetails = userDetails?.projects;
    const userRoleByCurrentProject = projectDetails?.find(
      (project: Project) => project.publisherId === activeProjectId
    )?.role;
    return userRoleByCurrentProject === UserRole.ADMIN;
  }, [userDetails?.projects]);

  return {
    getPermissions,
    setPermissions,
    userDetails,
    canUserEdit,
    isAdmin,
    isSuperAdmin,
    isViewerOrEditor,
    isSuperAdminByProject,
    isAdminByProject
  };
}
